import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";

import {
  getWalletDepositCoinAddress,
  getWalletDepositCoinNetworks,
  getWalletPortfolioCoin,
} from "../api/endpoints/wallet/wallet";
import type { CoinNetwork } from "../api/models/coinNetwork";
import type { DepositAddress } from "../api/models/depositAddress";
import type { PortfolioCoin } from "../api/models/portfolioCoin";
import { getBearerToken } from "../lib/authClient";
import { toNumber } from "../lib/numberFormat";

import "./DepositPage.css";

interface LocationState {
  code?: string;
  name?: string;
  icon?: string;
}

const authHeaders = () => {
  const token = getBearerToken();
  return token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
};

const formatUSD = (v: string | number) =>
  toNumber(v).toLocaleString("pt-BR", { style: "currency", currency: "USD" });

const formatQty = (v: string | number) =>
  toNumber(v).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  });

const truncateAddress = (addr: string) => {
  if (addr.length <= 12) return addr;
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
};

export default function DepositPage() {
  const navigate = useNavigate();
  const params = useParams<{ coin: string }>();
  const location = useLocation();
  const meta = (location.state as LocationState | null) ?? null;
  const coinCode = (params.coin ?? "").toUpperCase();

  const [portfolio, setPortfolio] = useState<PortfolioCoin | null>(null);
  const [networks, setNetworks] = useState<CoinNetwork[]>([]);
  const [selectedNetwork, setSelectedNetwork] = useState<string>("");
  const [address, setAddress] = useState<DepositAddress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  useEffect(() => {
    if (!coinCode) return;
    let cancelled = false;

    const fetchInitial = async () => {
      setLoading(true);
      setError(null);
      try {
        const [networksResp, portfolioResp] = await Promise.all([
          getWalletDepositCoinNetworks(coinCode, authHeaders()),
          getWalletPortfolioCoin(coinCode, authHeaders()),
        ]);
        if (cancelled) return;

        if (networksResp.status === 200 && "data" in networksResp.data) {
          const list = networksResp.data.data;
          setNetworks(list);
          if (list.length > 0) setSelectedNetwork(list[0].network);
        } else {
          const msg = (networksResp.data as { message?: string })?.message ??
            `Falha ao buscar redes (status ${networksResp.status}).`;
          setError(msg);
        }

        if (
          portfolioResp.status === 200 && "data" in portfolioResp.data
        ) {
          setPortfolio(portfolioResp.data.data);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Erro ao carregar depósito:", err);
        setError(err instanceof Error ? err.message : "Erro desconhecido");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchInitial();
    return () => {
      cancelled = true;
    };
  }, [coinCode]);

  useEffect(() => {
    if (!coinCode || !selectedNetwork) {
      setAddress(null);
      return;
    }
    let cancelled = false;

    const fetchAddress = async () => {
      setAddressLoading(true);
      setAddressError(null);
      try {
        const response = await getWalletDepositCoinAddress(
          coinCode,
          { network: selectedNetwork },
          authHeaders(),
        );
        if (cancelled) return;
        if (response.status === 200 && "data" in response.data) {
          setAddress(response.data.data);
        } else {
          setAddress(null);
          const msg = (response.data as { message?: string })?.message ??
            `Falha ao buscar endereço (status ${response.status}).`;
          setAddressError(msg);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Erro ao buscar endereço:", err);
        setAddress(null);
        setAddressError(
          err instanceof Error ? err.message : "Erro desconhecido",
        );
      } finally {
        if (!cancelled) setAddressLoading(false);
      }
    };

    fetchAddress();
    return () => {
      cancelled = true;
    };
  }, [coinCode, selectedNetwork]);

  const selectedNetworkMeta = useMemo(
    () => networks.find((n) => n.network === selectedNetwork) ?? null,
    [networks, selectedNetwork],
  );

  const handleCopy = async () => {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(address.address);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.warn("Falha ao copiar endereço:", err);
    }
  };

  const coinName = meta?.name ?? portfolio?.coin ?? coinCode;
  const coinIcon = meta?.icon;

  return (
    <div className="page-centered">
      <div className="deposit-wrap">
        <div className="deposit-topbar">
          <button
            type="button"
            className="deposit-close"
            onClick={() => navigate(-1)}
            aria-label="Fechar"
          >
            ×
          </button>
          <div className="deposit-topbar-title">
            <div className="deposit-topbar-heading">Receber</div>
            <div className="deposit-topbar-sub">{coinName}</div>
          </div>
          <span aria-hidden="true" className="deposit-topbar-spacer" />
        </div>

        {loading && <div className="deposit-status">Carregando...</div>}

        {error && !loading && (
          <div className="deposit-status deposit-status--error">{error}</div>
        )}

        {!loading && !error && (
          <>
            <div className="deposit-hero">
              <div className="deposit-hero-top">
                <span className="deposit-hero-label">Saldo Disponível</span>
                <div className="deposit-hero-coin">
                  {coinIcon && <img src={coinIcon} alt={coinCode} />}
                  <span>{coinCode}</span>
                </div>
              </div>
              <div className="deposit-hero-amount">
                {portfolio ? formatQty(portfolio.quantity) : "0,00"}
              </div>
              <div className="deposit-hero-sub">
                <span className="deposit-hero-usd">
                  {portfolio ? formatUSD(portfolio.totalUSD) : "—"}
                </span>
                {portfolio && (
                  <span
                    className={`deposit-hero-perf ${
                      toNumber(portfolio.performancePercent) >= 0
                        ? "deposit-hero-perf--up"
                        : "deposit-hero-perf--down"
                    }`}
                  >
                    {toNumber(portfolio.performancePercent) >= 0 ? "+" : ""}
                    {toNumber(portfolio.performancePercent).toFixed(2)}%
                  </span>
                )}
              </div>
              <div className="deposit-hero-actions">
                <button
                  type="button"
                  className="deposit-hero-btn deposit-hero-btn--soft"
                  onClick={() => navigate("/crypto-ramp")}
                >
                  Comprar
                </button>
                <button
                  type="button"
                  className="deposit-hero-btn deposit-hero-btn--ghost"
                  onClick={() => navigate("/orders")}
                >
                  Histórico
                </button>
              </div>
            </div>

            <div className="deposit-section">
              <label htmlFor="deposit-network" className="deposit-sectionLabel">
                Rede de recebimento
              </label>
              <div className="deposit-select">
                <select
                  id="deposit-network"
                  value={selectedNetwork}
                  onChange={(e) => setSelectedNetwork(e.target.value)}
                  disabled={networks.length === 0}
                >
                  {networks.length === 0 && (
                    <option value="">Nenhuma rede disponível</option>
                  )}
                  {networks.map((net) => (
                    <option key={net.network} value={net.network}>
                      {net.name}
                    </option>
                  ))}
                </select>
                <span className="deposit-select-chevron" aria-hidden="true">
                  ⌄
                </span>
              </div>
            </div>

            <div className="deposit-card">
              <div className="deposit-card-head">
                <span className="deposit-card-title">Seu Endereço</span>
                <button
                  type="button"
                  className="deposit-iconBtn"
                  onClick={handleCopy}
                  disabled={!address}
                  aria-label="Copiar endereço"
                  title={copied ? "Copiado!" : "Copiar"}
                >
                  {copied ? "✓" : "⧉"}
                </button>
              </div>

              <button
                type="button"
                className="deposit-addressBox"
                onClick={handleCopy}
                disabled={!address}
              >
                {addressLoading && <span>Gerando endereço...</span>}
                {!addressLoading && addressError && (
                  <span className="deposit-addressBox-error">
                    {addressError}
                  </span>
                )}
                {!addressLoading && !addressError && address && (
                  <span className="deposit-addressBox-text">
                    {truncateAddress(address.address)}
                  </span>
                )}
                {!addressLoading && !addressError && !address && (
                  <span>—</span>
                )}
              </button>

              <button
                type="button"
                className="deposit-qrBtn"
                disabled={!address}
                onClick={() => setQrOpen(true)}
              >
                <span aria-hidden="true">▦</span> Ver QR Code
              </button>
            </div>

            <div className="deposit-important">
              <span className="deposit-important-icon" aria-hidden="true">
                ⓘ
              </span>
              <div>
                <div className="deposit-important-title">Importante</div>
                <div className="deposit-important-text">
                  Envie apenas {coinCode}
                  {selectedNetworkMeta
                    ? ` (${selectedNetworkMeta.name})`
                    : ""} para este endereço na rede selecionada. Outros ativos
                  podem ser perdidos.
                  {selectedNetworkMeta?.minDeposit
                    ? ` Depósito mínimo: ${selectedNetworkMeta.minDeposit} ${coinCode}.`
                    : ""}
                </div>
              </div>
            </div>
          </>
        )}

        {qrOpen && address && (
          <div
            className="deposit-qrModal"
            role="dialog"
            aria-modal="true"
            onClick={() => setQrOpen(false)}
          >
            <div
              className="deposit-qrModal-box"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="deposit-qrModal-head">
                <div>
                  <div className="deposit-qrModal-title">QR Code</div>
                  <div className="deposit-qrModal-sub">
                    {coinCode}
                    {selectedNetworkMeta ? ` · ${selectedNetworkMeta.name}` : ""}
                  </div>
                </div>
                <button
                  type="button"
                  className="deposit-close"
                  onClick={() => setQrOpen(false)}
                  aria-label="Fechar QR Code"
                >
                  ×
                </button>
              </div>

              <div className="deposit-qrModal-code">
                <QRCodeSVG
                  value={address.address}
                  size={224}
                  level="M"
                  marginSize={2}
                />
              </div>

              <div className="deposit-qrModal-address">
                {address.address}
              </div>

              <button
                type="button"
                className="deposit-qrBtn"
                onClick={handleCopy}
              >
                {copied ? "Copiado!" : "Copiar endereço"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
